import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { isContentTypeEnabledInCurrentEnv } from '@atlassian/content-types-rollout';
import { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';
import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import { type EntityATI, type LoomEntityATI } from '@atlassian/search-client/common/types';
import {
	type TProductDataRegions as ProductDataRegions,
	type TProductEdition as ProductEdition,
} from '@atlassian/search-client/fetch-search-config';
import { extractTraceInfoFromResponse } from '@atlassian/search-client/network-trace-info';
import { type NetworkCallbacks } from '@atlassian/search-client/persist-refresh';
import {
	type SearchPageQueryVariables,
	type ThirdPartyFilters,
} from '@atlassian/search-client/query-generator';
import { searchDialogQuery } from '@atlassian/search-client/search-page';
import { type SearchPageNode } from '@atlassian/search-client/search-page/types';
import { getEdgeRegionHint } from '@atlassian/search-common/get-edge-region-hint';
import { getBackendExperiment } from '@atlassian/search-experiment/backend';
import { type SearchExperimentLayer } from '@atlassian/search-experiment/dynamic/types';
import { type BackendExperiment, type ExperimentConfig } from '@atlassian/search-experiment/types';
import type { SearchUseCaseId } from '@atlassian/search-common/search-use-case-id';

import { DEFAULT_CLOUD_GRAPH_DEVINFO_PULL_REQUEST_ENTITY_ATI } from '../../../../common/constants/default-cloud-graph-devinfo-pull-request-entity-ati';
import { DEFAULT_CLOUD_GRAPH_DEVINFO_REPOSITORY_ENTITY_ATI } from '../../../../common/constants/default-cloud-graph-devinfo-repository-entity-ati';
import {
	JIRA_BOARD_ENTITY_ATI,
	JIRA_DASHBOARD_ENTITY_ATI,
	JIRA_FILTER_ENTITY_ATI,
	JIRA_ISSUE_ENTITY_ATI,
	JIRA_PLAN_ENTITY_ATI,
	JIRA_PROJECT_ENTITY_ATI,
} from '../../../../common/constants/entities';
import { type PrimaryProductKey } from '../../../../common/constants/product-keys';
import { type ThirdPartyConfigsBootstrap } from '../../../../common/constants/schemas/3p-config';
import { type SelectedFiltersType } from '../../../../common/constants/types';
import { isLegacyCodeSearchEnabled } from '../../../../common/utils/code-search-mode';
import { serveQfFastJiraHydrationExperiment } from '../../../../common/utils/experiments';
import { getEmptySelectedFilters, getThirdPartyFilters } from '../../../../common/utils/filters';
import { isHello } from '../../../../common/utils/is-hello';
import type { UseTypeEntitiesResult } from '../../../store/search/get-type-entities';

import {
	validateAssetsResponse,
	validateAtlasResponse,
	validateConfluenceEntitiesResponse,
	validateConfluenceSpacesResponse,
	validateJiraEntitiesResponse,
	validateJiraIssuesResponse,
	validateLoomEntitiesResponse,
	validateMercuryResponse,
	validateTalentResponse,
} from './flat-validators';

const experience = 'quick-find';

/**
 * Builds merged headers combining atl-attribution and region steering headers.
 * Feature flag check for atl-attribution is handled internally by buildAtlAttributionHeaders.
 */
const buildMergedHeaders = (
	cloudId: string,
	activationId: string | undefined,
	sourceProduct: PrimaryProductKey | undefined,
	productDataRegions: ProductDataRegions | undefined,
): Record<string, string> | undefined => {
	// Feature flag check is handled internally by buildAtlAttributionHeaders
	const atlAttributionHeaders = buildAtlAttributionHeaders({
		cloudId,
		activationId,
		product: sourceProduct,
	});

	const regionSteeringHeaders =
		sourceProduct && productDataRegions
			? { 'atl-edge-region-hint': getEdgeRegionHint(sourceProduct, productDataRegions) }
			: undefined;

	if (!atlAttributionHeaders && !regionSteeringHeaders) {
		return undefined;
	}

	return {
		...regionSteeringHeaders,
		...atlAttributionHeaders,
	};
};

export const getThirdPartyFiltersForQuickFind: (
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
) => ThirdPartyFilters = (thirdPartyConfigs: ThirdPartyConfigsBootstrap) => {
	const excluded3PProducts: ProductKeys3P[] = [
		ProductKeys3P.Slack,
		ProductKeys3P.Teams,
		ProductKeys3P.Gmail,
		ProductKeys3P.OutlookMail,
	];

	// Adding additional 3P products not included in thirdPartyConfigs
	const additional3PProducts: ProductKeys3P[] = [
		ProductKeys3P.Loom, // Loom is 1P product but treated as 3P in API filters
	];

	const thirdPartyKeys = Object.keys(thirdPartyConfigs) as ProductKeys3P[];

	const quickFind3PProducts: ProductKeys3P[] = additional3PProducts
		.concat(thirdPartyKeys)
		.filter((productKey) => !excluded3PProducts.includes(productKey));

	return getThirdPartyFilters(
		quickFind3PProducts,
		false, // titleMatchOnly value
		[], // it will search all subtypes if no entities are provided
		thirdPartyConfigs,
	);
};

export const serve3PinQuickFind = (thirdPartyConfigs: ThirdPartyConfigsBootstrap): boolean => {
	// Prerequisite: We should not request 3P in Quick Find if the user does not have any 3P authenticated
	const isEligible3PUser = Object.keys(thirdPartyConfigs).some(
		(productKey) => thirdPartyConfigs[productKey]?.userNeedsOAuth === false,
	);

	return isEligible3PUser;
};

/**
 * Returns a copy of `filters` with `entity` added to the GitHub `thirdPartyProducts` entry's
 * `entities` array. Scopes the requested entity to GitHub only (vs the top-level `entities`
 * field which fans the request out to every connected 3P provider). No-op if no GitHub entry
 * exists.
 */
const addEntityToGitHubProduct = (
	filters: ThirdPartyFilters,
	entity: string,
): ThirdPartyFilters => ({
	...filters,
	thirdPartyProducts: filters?.thirdPartyProducts?.map((product) => {
		if (product.product !== 'github') {
			return product;
		}
		const entities = new Set([...(product.entities ?? []), entity]);
		return { ...product, entities: [...entities] };
	}),
});

const GITHUB_PRODUCT_KEYS: ProductKeys3P[] = [
	ProductKeys3P.Github,
	ProductKeys3P.ForgePioGithubDev,
	ProductKeys3P.ForgePioGithubProd,
];

const hasGitHubConnected = (thirdPartyConfigs: ThirdPartyConfigsBootstrap): boolean =>
	GITHUB_PRODUCT_KEYS.some((key) => thirdPartyConfigs[key]?.userNeedsOAuth === false);

const hasGitHubImpersonationEnabled = (thirdPartyConfigs: ThirdPartyConfigsBootstrap): boolean =>
	!!(
		thirdPartyConfigs[ProductKeys3P.Github]?.userNeedsOAuth &&
		thirdPartyConfigs[ProductKeys3P.Github]?.impersonationEnabled
	);

export const serveGitHub3PInJiraQuickFind = (
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
): {
	serveGitHub3P: boolean;
} => {
	return {
		serveGitHub3P:
			hasGitHubConnected(thirdPartyConfigs) || hasGitHubImpersonationEnabled(thirdPartyConfigs),
	};
};

export const getGitHubThirdPartyFiltersForQuickFind: (
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
) => ThirdPartyFilters = (thirdPartyConfigs: ThirdPartyConfigsBootstrap) => {
	const gitHubThirdPartyConfigs: ThirdPartyConfigsBootstrap = {};
	for (const key of GITHUB_PRODUCT_KEYS) {
		if (thirdPartyConfigs[key]) {
			gitHubThirdPartyConfigs[key] = thirdPartyConfigs[key];
		}
	}

	return getThirdPartyFilters(
		GITHUB_PRODUCT_KEYS,
		false, // titleMatchOnly value
		[], // search all subtypes
		gitHubThirdPartyConfigs,
	);
};

export const fetchConfluenceAnd3PEntities: (
	aggAbsoluteUrl: string | undefined,
	useCaseId: SearchUseCaseId,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	filteredEntities: string[],
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => Promise<{
	fetchResults: EssentialSearchResult[];
	fetchEntitiesRequestId: (string | null) | undefined;
	fetchEntitiesTraceId: (string | null) | undefined;
	errors: import('@atlassian/search-client').AggError[];
	autocompleteSuggestions: string[] | undefined;
}> = async (
	aggAbsoluteUrl: string | undefined,
	useCaseId: SearchUseCaseId,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	filteredEntities: string[],
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => {
	// TODO: replace all the hard-coded values of filters with the ones passed on from selectedFilters
	const { commonFilters, confluenceFilters, mercuryFilters, compassFilters, assetsFilters } =
		selectedFilters || getEmptySelectedFilters();

	const serve3P = serve3PinQuickFind(thirdPartyConfigs);

	let thirdPartyFilters: ThirdPartyFilters = serve3P
		? getThirdPartyFiltersForQuickFind(thirdPartyConfigs)
		: { titleMatchOnly: true, subtypes: [] };

	const supportedEntities = [
		'ati:cloud:confluence:page',
		'ati:cloud:confluence:blogpost',
		'ati:cloud:confluence:attachment',
		'ati:cloud:confluence:whiteboard',
		...(isContentTypeEnabledInCurrentEnv('slide') &&
		!fg('cc-mui-slides-opted-out') &&
		FeatureGates.getExperimentValue<boolean>('cc-mui-slides-experiment', 'isEnabled', false, {
			fireExperimentExposure: true,
		}) === true
			? (['ati:cloud:confluence:slide'] as const)
			: []),
		'ati:cloud:confluence:database',
		'ati:cloud:confluence:embed',
		'ati:cloud:confluence:folder',
		...(serve3P ? ['ati:cloud:graph:document'] : []),
		...(fg('quickfind-smartlinks') || serve3P ? ['ati:cloud:graph:remote-link'] : []),
	] as EntityATI[];

	const entities = supportedEntities.filter((supportedEntity) =>
		filteredEntities.includes(supportedEntity),
	);

	// GitHub code search is independently gated: any user with GitHub connected and the gate
	// on gets code results, regardless of which other 3P experiment cohort they're in.
	// Scope the document entity to the GitHub thirdPartyProducts entry (not the top-level
	// entities array) so we don't fan the request out to every other 3P provider.
	// Quick Find code search is legacy-only: in code search M1 mode only Full Page Search
	// serves code, so this path stays off.
	const isGitHubConnectedForConfluence = GITHUB_PRODUCT_KEYS.some(
		(key) => thirdPartyConfigs[key]?.userNeedsOAuth === false,
	);
	if (isGitHubConnectedForConfluence && isLegacyCodeSearchEnabled()) {
		thirdPartyFilters = addEntityToGitHubProduct(thirdPartyFilters, 'ati:cloud:graph:document');
	}

	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience,
		tenantId: cloudId,
		thirdPartyFilters,
	});

	setRequestedBackendExperiments(backendExperiment);

	const { experimentLayers, experimentId, shadowExperimentId } = backendExperiment || {};

	const variables = {
		experience,
		useCaseId,
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:confluence::site/${cloudId}`,
		tenantId: cloudId,
		limit: 7,
		commonFilters,
		confluenceFilters,
		assetsFilters,
		jiraFilters: {
			issueFilter: {
				projectARIs: [],
				assigneeARIs: [],
				reporterARIs: [],
				commenterARIs: [],
				issueTypeIDs: [],
				issueLabels: [],
				issueTypes: [],
				statusCategories: [],
				watcherARIs: [],
			},
		},
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		entities,
		sort: [],
		searchSessionId,
		sessionId,
		sourceProduct,
		experimentId: experimentId ?? undefined,
		shadowExperimentId: shadowExperimentId ?? undefined,
		experimentLayers: mapExperimentLayers(experimentLayers),
		includeSocialSignals: false,
		includeUserSignals: false,
		includeSpaceSubtype: false,
		includeConfluenceEntity: false,
		skipFieldsFor3pInQuickFind: serve3P,
		includeGithubEntityHydration: false,
		includeAutocompleteSuggestions: fg('fe_quickfind_autocomplete_suggestions'),
	} satisfies SearchPageQueryVariables;

	const headers = buildMergedHeaders(cloudId, activationId, sourceProduct, productDataRegions);

	networkCallbacks?.onStart();

	try {
		const response = await searchDialogQuery({
			variables,
			aggAbsoluteUrl,
			headers,
		});

		const traceInfo = extractTraceInfoFromResponse(response);

		if (networkCallbacks) {
			const validationResult = validateConfluenceEntitiesResponse(response);
			if (!validationResult.success) {
				networkCallbacks.onError(
					validationResult.error.name,
					validationResult.error.message,
					traceInfo,
				);
			} else {
				networkCallbacks.onSuccess(traceInfo);
			}
		}

		const nodes = response.data?.search?.results.edges.map((edge) => edge.node) || [];
		const autocompleteSuggestions = response.data?.search?.results.autocompleteSuggestions;
		return {
			fetchResults: processAndValidateNodes(nodes),
			fetchEntitiesRequestId: response.extensions?.gateway?.request_id,
			fetchEntitiesTraceId: response.extensions?.gateway?.trace_id,
			errors: response.errors || [],
			autocompleteSuggestions,
		};
	} catch (error: any) {
		// Network failure caught here
		if (networkCallbacks) {
			networkCallbacks.onError(
				error.name || 'NetworkError',
				error.message || 'Failed to fetch search results',
			);
		}
		// Return empty results to allow graceful degradation
		return {
			fetchResults: [],
			errors: [],
			fetchEntitiesRequestId: undefined,
			fetchEntitiesTraceId: undefined,
			autocompleteSuggestions: undefined,
		};
	}
};

export const fetchLoomEntities = async (
	aggAbsoluteUrl: string | undefined,
	useCaseId: SearchUseCaseId,
	query: string,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
	selectedTypeEntities: readonly EntityATI[] = [],
): Promise<{
	fetchResults: EssentialSearchResult[];
	fetchEntitiesRequestId: (string | null) | undefined;
	fetchEntitiesTraceId: (string | null) | undefined;
	errors: import('@atlassian/search-client').AggError[];
}> => {
	const sourceProduct = ProductKeys3P.Loom;
	const {
		commonFilters,
		confluenceFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		assetsFilters,
	} = getEmptySelectedFilters();
	const isLoomRovoSearchEnabled = fg('loom-rovo-search');
	const loomFilters = selectedFilters?.loomFilters;
	const nativeTypeEntities: LoomEntityATI[] = isLoomRovoSearchEnabled
		? selectedTypeEntities.filter((entity): entity is LoomEntityATI =>
				entity.startsWith('ati:cloud:loom:'),
			)
		: [];
	const hasSelectedLoomVideoFilters =
		isLoomRovoSearchEnabled &&
		(Boolean(loomFilters?.spaceIds?.length) ||
			Boolean(loomFilters?.folderIds?.length) ||
			Boolean(loomFilters?.tagNames?.length) ||
			Boolean(loomFilters?.videoTypes?.length));
	const valueFilterEntities: LoomEntityATI[] = [
		...(hasSelectedLoomVideoFilters ? (['ati:cloud:loom:video'] as const) : []),
		...(isLoomRovoSearchEnabled && loomFilters?.playlistIds?.length
			? (['ati:cloud:loom:playlist'] as const)
			: []),
	];
	const entities: EntityATI[] =
		nativeTypeEntities.length > 0
			? nativeTypeEntities
			: valueFilterEntities.length > 0
				? valueFilterEntities
				: ['ati:cloud:graph:video'];
	const usesNativeLoomEntities = entities.some((entity) => entity.startsWith('ati:cloud:loom:'));
	const thirdPartyFilters = usesNativeLoomEntities
		? undefined
		: getThirdPartyFilters([ProductKeys3P.Loom], false, [], thirdPartyConfigs, false);
	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience,
		tenantId: cloudId,
		...(thirdPartyFilters ? { thirdPartyFilters } : {}),
	});

	setRequestedBackendExperiments(backendExperiment);

	const { experimentLayers, experimentId, shadowExperimentId } = backendExperiment || {};
	const variables = {
		experience,
		useCaseId,
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:loom::site/${cloudId}`,
		tenantId: cloudId,
		limit: 7,
		commonFilters,
		confluenceFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		assetsFilters,
		...(isLoomRovoSearchEnabled ? { loomFilters } : {}),
		...(thirdPartyFilters ? { thirdPartyFilters } : {}),
		entities,
		sort: [],
		searchSessionId,
		sessionId,
		sourceProduct,
		experimentId: experimentId ?? undefined,
		shadowExperimentId: shadowExperimentId ?? undefined,
		experimentLayers: mapExperimentLayers(experimentLayers),
		includeSocialSignals: false,
		includeUserSignals: false,
		includeSpaceSubtype: false,
		includeConfluenceEntity: false,
		skipFieldsFor3pInQuickFind: true,
		includeGithubEntityHydration: false,
		includeLoomVideoMetadata: isLoomRovoSearchEnabled,
		// TODO: Enable Loom autocomplete after its suggestions are forwarded through Quick Find.
		includeAutocompleteSuggestions: false,
	} satisfies SearchPageQueryVariables;

	const headers = buildMergedHeaders(cloudId, activationId, sourceProduct, productDataRegions);

	networkCallbacks?.onStart();

	try {
		const response = await searchDialogQuery({
			variables,
			aggAbsoluteUrl,
			headers,
		});

		const traceInfo = extractTraceInfoFromResponse(response);

		if (networkCallbacks) {
			const validationResult = validateLoomEntitiesResponse(response);
			if (!validationResult.success) {
				networkCallbacks.onError(
					validationResult.error.name,
					validationResult.error.message,
					traceInfo,
				);
			} else {
				networkCallbacks.onSuccess(traceInfo);
			}
		}

		const nodes = response.data?.search?.results.edges.map((edge) => edge.node) || [];
		return {
			fetchResults: processAndValidateNodes(nodes),
			fetchEntitiesRequestId: response.extensions?.gateway?.request_id,
			fetchEntitiesTraceId: response.extensions?.gateway?.trace_id,
			errors: response.errors || [],
		};
	} catch (error: any) {
		if (networkCallbacks) {
			networkCallbacks.onError(
				error.name || 'NetworkError',
				error.message || 'Failed to fetch search results',
			);
		}
		return {
			fetchResults: [],
			errors: [],
			fetchEntitiesRequestId: undefined,
			fetchEntitiesTraceId: undefined,
		};
	}
};

export const fetchConfluenceSpaces: (
	aggAbsoluteUrl: string | undefined,
	useCaseId: SearchUseCaseId,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => Promise<{
	fetchResults: EssentialSearchResult[];
	fetchSpacesRequestId: (string | null) | undefined;
	errors: import('@atlassian/search-client').AggError[];
}> = async (
	aggAbsoluteUrl: string | undefined,
	useCaseId: SearchUseCaseId,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => {
	// TODO: replace all the hard-coded values of filters with the ones passed on from selectedFilters
	// and remove experiementId and shadowExperimentId as these are no longer used
	const { commonFilters, confluenceFilters, mercuryFilters, compassFilters, assetsFilters } =
		selectedFilters || getEmptySelectedFilters();

	const entities: EntityATI[] = ['ati:cloud:confluence:space'];

	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience,
		tenantId: cloudId,
	});

	const { experimentLayers, experimentId, shadowExperimentId } = backendExperiment || {};

	const variables = {
		experience,
		useCaseId,
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:confluence::site/${cloudId}`,
		tenantId: cloudId,
		limit: 1,
		commonFilters,
		confluenceFilters,
		assetsFilters,
		jiraFilters: {
			issueFilter: {
				projectARIs: [],
				assigneeARIs: [],
				reporterARIs: [],
				commenterARIs: [],
				issueTypeIDs: [],
				issueLabels: [],
				issueTypes: [],
				statusCategories: [],
				watcherARIs: [],
			},
		},
		mercuryFilters,
		compassFilters,
		thirdPartyFilters: {
			titleMatchOnly: false,
			subtypes: [],
		},
		entities,
		sort: [],
		searchSessionId,
		sessionId,
		sourceProduct,
		experimentId: experimentId ?? undefined,
		shadowExperimentId: shadowExperimentId ?? undefined,
		experimentLayers: mapExperimentLayers(experimentLayers),
		includeSocialSignals: false,
		includeSpaceSubtype: false,
	} satisfies SearchPageQueryVariables;

	const headers = buildMergedHeaders(cloudId, activationId, sourceProduct, productDataRegions);

	networkCallbacks?.onStart();

	try {
		const response = await searchDialogQuery({
			variables,
			aggAbsoluteUrl,
			headers,
		});

		const traceInfo = extractTraceInfoFromResponse(response);

		if (networkCallbacks) {
			const validationResult = validateConfluenceSpacesResponse(response);
			if (!validationResult.success) {
				networkCallbacks.onError(
					validationResult.error.name,
					validationResult.error.message,
					traceInfo,
				);
			} else {
				networkCallbacks.onSuccess(traceInfo);
			}
		}

		const nodes = response.data?.search?.results.edges.map((edge) => edge.node) || [];
		return {
			fetchResults: processAndValidateNodes(nodes),
			fetchSpacesRequestId: response.extensions?.gateway?.request_id,
			errors: response.errors || [],
		};
	} catch (error: any) {
		// Network failure caught here
		if (networkCallbacks) {
			networkCallbacks.onError(
				error.name || 'NetworkError',
				error.message || 'Failed to fetch search results',
			);
		}
		// Return empty results to allow graceful degradation
		return {
			fetchResults: [],
			errors: [],
			fetchSpacesRequestId: undefined,
		};
	}
};

export const fetchAtlasEntities: (
	aggAbsoluteUrl: string | undefined,
	useCaseId: SearchUseCaseId,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => Promise<{
	fetchResults: EssentialSearchResult[];
	fetchEntitiesRequestId: (string | null) | undefined;
	fetchEntitiesTraceId: (string | null) | undefined;
	errors: import('@atlassian/search-client').AggError[];
}> = async (
	aggAbsoluteUrl: string | undefined,
	useCaseId: SearchUseCaseId,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => {
	// TODO: replace all the hard-coded values of filters with the ones passed on from selectedFilters
	// and remove experiementId and shadowExperimentId as these are no longer used
	const { commonFilters, mercuryFilters, compassFilters, assetsFilters } =
		selectedFilters || getEmptySelectedFilters();

	const serve3P = serve3PinQuickFind(thirdPartyConfigs);
	const thirdPartyFilters: ThirdPartyFilters = serve3P
		? getThirdPartyFiltersForQuickFind(thirdPartyConfigs)
		: { titleMatchOnly: false, subtypes: [] };

	const entities: EntityATI[] = [
		'ati:cloud:townsquare:project',
		'ati:cloud:townsquare:goal',
		'ati:cloud:townsquare:tag',
		'ati:cloud:confluence:page',
		'ati:cloud:confluence:blogpost',
		JIRA_ISSUE_ENTITY_ATI,
		...(serve3P ? (['ati:cloud:graph:document'] as EntityATI[]) : []),
		...(serve3P ? (['ati:cloud:graph:remote-link'] as EntityATI[]) : []),
	];

	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience,
		tenantId: cloudId,
	});

	setRequestedBackendExperiments(backendExperiment);

	const { experimentLayers, experimentId, shadowExperimentId } = backendExperiment || {};

	const variables = {
		experience,
		useCaseId,
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:confluence::site/${cloudId}`,
		tenantId: cloudId,
		limit: 6,
		commonFilters,
		assetsFilters,
		confluenceFilters: {
			spacesFilter: [],
			contributorsFilter: [],
			ancestorIdsFilter: [],
			range: [],
			owners: [],
			labelsFilter: [],
			titleMatchOnly: true,
			contentStatuses: [],
			containerStatus: [],
		},
		jiraFilters: {
			issueFilter: {
				projectARIs: [],
				assigneeARIs: [],
				reporterARIs: [],
				commenterARIs: [],
				issueTypeIDs: [],
				issueLabels: [],
				issueTypes: [],
				statusCategories: [],
				watcherARIs: [],
			},
		},
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		entities,
		sort: [],
		searchSessionId,
		sessionId,
		sourceProduct,
		experimentId,
		shadowExperimentId,
		experimentLayers: mapExperimentLayers(experimentLayers),
		includeSocialSignals: false,
		includeSpaceSubtype: false,
		skipFieldsFor3pInQuickFind: serve3P,
		includeGithubEntityHydration: false,
	} satisfies SearchPageQueryVariables;

	const headers = buildMergedHeaders(cloudId, activationId, sourceProduct, productDataRegions);

	networkCallbacks?.onStart();

	try {
		const response = await searchDialogQuery({
			variables,
			aggAbsoluteUrl,
			headers,
		});

		const traceInfo = extractTraceInfoFromResponse(response);

		if (networkCallbacks) {
			const validationResult = validateAtlasResponse(response);
			if (!validationResult.success) {
				networkCallbacks.onError(
					validationResult.error.name,
					validationResult.error.message,
					traceInfo,
				);
			} else {
				networkCallbacks.onSuccess(traceInfo);
			}
		}

		const nodes = response.data?.search?.results.edges.map((edge) => edge.node) || [];
		return {
			fetchResults: processAndValidateNodes(nodes),
			fetchEntitiesRequestId: response.extensions?.gateway?.request_id,
			fetchEntitiesTraceId: response.extensions?.gateway?.trace_id,
			errors: response.errors || [],
		};
	} catch (error: any) {
		// Network failure caught here
		if (networkCallbacks) {
			networkCallbacks.onError(
				error.name || 'NetworkError',
				error.message || 'Failed to fetch search results',
			);
		}
		// Return empty results to allow graceful degradation
		return {
			fetchResults: [],
			errors: [],
			fetchEntitiesRequestId: undefined,
			fetchEntitiesTraceId: undefined,
		};
	}
};

export const fetchMercuryEntities: (
	aggAbsoluteUrl: string | undefined,
	useCaseId: SearchUseCaseId,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => Promise<{
	fetchResults: EssentialSearchResult[];
	fetchEntitiesRequestId: (string | null) | undefined;
	fetchEntitiesTraceId: (string | null) | undefined;
	errors: import('@atlassian/search-client').AggError[];
}> = async (
	aggAbsoluteUrl: string | undefined,
	useCaseId: SearchUseCaseId,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => {
	// TODO: replace all the hard-coded values of filters with the ones passed on from selectedFilters
	// and remove experiementId and shadowExperimentId as these are no longer used
	const {
		commonFilters,
		confluenceFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		assetsFilters,
		thirdPartyFilters: selectedThirdPartyFilters,
	} = selectedFilters || getEmptySelectedFilters();

	// Respect explicit user-selected 3P filters if provided; otherwise fall back to the empty default.
	const thirdPartyFilters: ThirdPartyFilters =
		selectedFilters?.thirdPartyFilters ?? selectedThirdPartyFilters;

	const passionfruitAskEntity: EntityATI = 'ati:cloud:passionfruit:ask';
	const mercuryRiskEntity: EntityATI = 'ati:cloud:mercury:risk';
	const organizationEntity: EntityATI = 'ati:cloud:mercury:organization';
	const organizationStatusUpdateEntity: EntityATI = 'ati:cloud:mercury:organization-status-update';
	const entities: EntityATI[] = [
		'ati:cloud:mercury:focus-area',
		'ati:cloud:mercury:focus-area-status-update',
		...(fg('platform_passionfruit_search_onboarding_enabled') ? [passionfruitAskEntity] : []),
		...(fg('platform_mercury_risk_search_ui_enabled') ? [mercuryRiskEntity] : []),
		...(fg('platform_organization_search_onboarding_enabled')
			? [organizationEntity, organizationStatusUpdateEntity]
			: []),
	];

	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience,
		tenantId: cloudId,
	});

	setRequestedBackendExperiments(backendExperiment);

	const { experimentLayers, experimentId, shadowExperimentId } = backendExperiment || {};

	const variables = {
		experience,
		useCaseId,
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:confluence::site/${cloudId}`,
		tenantId: cloudId,
		limit: 6,
		commonFilters: commonFilters,
		confluenceFilters,
		assetsFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		entities,
		sort: [],
		searchSessionId,
		sessionId,
		sourceProduct,
		experimentId,
		shadowExperimentId,
		experimentLayers: mapExperimentLayers(experimentLayers),
		includeSocialSignals: false,
		includeSpaceSubtype: false,
		includeGithubEntityHydration: false,
	} satisfies SearchPageQueryVariables;

	const headers = buildMergedHeaders(cloudId, activationId, sourceProduct, productDataRegions);

	networkCallbacks?.onStart();

	try {
		const response = await searchDialogQuery({
			variables,
			aggAbsoluteUrl,
			headers,
		});

		const traceInfo = extractTraceInfoFromResponse(response);

		if (networkCallbacks) {
			const validationResult = validateMercuryResponse(response);
			if (!validationResult.success) {
				networkCallbacks.onError(
					validationResult.error.name,
					validationResult.error.message,
					traceInfo,
				);
			} else {
				networkCallbacks.onSuccess(traceInfo);
			}
		}

		const nodes = response.data?.search?.results.edges.map((edge) => edge.node) || [];
		return {
			fetchResults: processAndValidateNodes(nodes),
			fetchEntitiesRequestId: response.extensions?.gateway?.request_id,
			fetchEntitiesTraceId: response.extensions?.gateway?.trace_id,
			errors: response.errors || [],
		};
	} catch (error: any) {
		// Network failure caught here
		if (networkCallbacks) {
			networkCallbacks.onError(
				error.name || 'NetworkError',
				error.message || 'Failed to fetch search results',
			);
		}
		// Return empty results to allow graceful degradation
		return {
			fetchResults: [],
			errors: [],
			fetchEntitiesRequestId: undefined,
			fetchEntitiesTraceId: undefined,
		};
	}
};

export const fetchTalentEntities: (
	aggAbsoluteUrl: string | undefined,
	useCaseId: SearchUseCaseId,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => Promise<{
	fetchResults: EssentialSearchResult[];
	fetchEntitiesRequestId: (string | null) | undefined;
	fetchEntitiesTraceId: (string | null) | undefined;
	errors: import('@atlassian/search-client').AggError[];
}> = async (
	aggAbsoluteUrl: string | undefined,
	useCaseId: SearchUseCaseId,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => {
	// TODO: replace all the hard-coded values of filters with the ones passed on from selectedFilters
	// and remove experiementId and shadowExperimentId as these are no longer used
	const {
		commonFilters,
		confluenceFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		assetsFilters,
		talentFilters,
		thirdPartyFilters: selectedThirdPartyFilters,
	} = selectedFilters || getEmptySelectedFilters();

	// Respect explicit user-selected 3P filters if provided; otherwise fall back to the empty default.
	const thirdPartyFilters: ThirdPartyFilters =
		selectedFilters?.thirdPartyFilters ?? selectedThirdPartyFilters;

	const entities: EntityATI[] = ['ati:cloud:radar:position'];

	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience,
		tenantId: cloudId,
	});

	setRequestedBackendExperiments(backendExperiment);

	const { experimentLayers, experimentId, shadowExperimentId } = backendExperiment || {};

	const variables = {
		experience,
		useCaseId,
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:confluence::site/${cloudId}`,
		tenantId: cloudId,
		limit: 6,
		commonFilters: commonFilters,
		confluenceFilters,
		assetsFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		talentFilters,
		entities,
		sort: [],
		searchSessionId,
		sessionId,
		sourceProduct,
		experimentId,
		shadowExperimentId,
		experimentLayers: mapExperimentLayers(experimentLayers),
		includeSocialSignals: false,
		includeSpaceSubtype: false,
		includeGithubEntityHydration: false,
	} satisfies SearchPageQueryVariables;

	const headers = buildMergedHeaders(cloudId, activationId, sourceProduct, productDataRegions);

	networkCallbacks?.onStart();

	try {
		const response = await searchDialogQuery({
			variables,
			aggAbsoluteUrl,
			headers,
		});

		const traceInfo = extractTraceInfoFromResponse(response);

		if (networkCallbacks) {
			const validationResult = validateTalentResponse(response);
			if (!validationResult.success) {
				networkCallbacks.onError(
					validationResult.error.name,
					validationResult.error.message,
					traceInfo,
				);
			} else {
				networkCallbacks.onSuccess(traceInfo);
			}
		}

		const nodes = response.data?.search?.results.edges.map((edge) => edge.node) || [];
		return {
			fetchResults: processAndValidateNodes(nodes),
			fetchEntitiesRequestId: response.extensions?.gateway?.request_id,
			fetchEntitiesTraceId: response.extensions?.gateway?.trace_id,
			errors: response.errors || [],
		};
	} catch (error: any) {
		// Network failure caught here
		if (networkCallbacks) {
			networkCallbacks.onError(
				error.name || 'NetworkError',
				error.message || 'Failed to fetch search results',
			);
		}
		// Return empty results to allow graceful degradation
		return {
			fetchResults: [],
			errors: [],
			fetchEntitiesRequestId: undefined,
			fetchEntitiesTraceId: undefined,
		};
	}
};

export const fetchAssetsEntities: (
	aggAbsoluteUrl: string | undefined,
	useCaseId: SearchUseCaseId,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => Promise<{
	fetchResults: EssentialSearchResult[];
	fetchEntitiesRequestId: (string | null) | undefined;
	fetchEntitiesTraceId: (string | null) | undefined;
	errors: import('@atlassian/search-client').AggError[];
}> = async (
	aggAbsoluteUrl: string | undefined,
	useCaseId: SearchUseCaseId,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => {
	// TODO: replace all the hard-coded values of filters with the ones passed on from selectedFilters
	// and remove experiementId and shadowExperimentId as these are no longer used
	const {
		commonFilters,
		confluenceFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		assetsFilters,
		thirdPartyFilters: selectedThirdPartyFilters,
	} = selectedFilters || getEmptySelectedFilters();

	// Respect explicit user-selected 3P filters if provided; otherwise fall back to the empty default.
	const thirdPartyFilters: ThirdPartyFilters =
		selectedFilters?.thirdPartyFilters ?? selectedThirdPartyFilters;

	const entities: EntityATI[] = [
		'ati:cloud:cmdb:object',
		'ati:cloud:cmdb:schema',
		'ati:cloud:cmdb:type',
	];

	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience,
		tenantId: cloudId,
	});

	setRequestedBackendExperiments(backendExperiment);

	const { experimentLayers, experimentId, shadowExperimentId } = backendExperiment || {};

	const variables = {
		experience,
		useCaseId,
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:confluence::site/${cloudId}`,
		tenantId: cloudId,
		limit: 6,
		commonFilters: commonFilters,
		confluenceFilters,
		assetsFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		entities,
		sort: [],
		searchSessionId,
		sessionId,
		sourceProduct,
		experimentId,
		shadowExperimentId,
		experimentLayers: mapExperimentLayers(experimentLayers),
		includeSocialSignals: false,
		includeSpaceSubtype: false,
		includeGithubEntityHydration: false,
	} satisfies SearchPageQueryVariables;

	const headers = buildMergedHeaders(cloudId, activationId, sourceProduct, productDataRegions);

	networkCallbacks?.onStart();

	try {
		const response = await searchDialogQuery({
			variables,
			aggAbsoluteUrl,
			headers,
		});

		const traceInfo = extractTraceInfoFromResponse(response);

		if (networkCallbacks) {
			const validationResult = validateAssetsResponse(response);
			if (!validationResult.success) {
				networkCallbacks.onError(
					validationResult.error.name,
					validationResult.error.message,
					traceInfo,
				);
			} else {
				networkCallbacks.onSuccess(traceInfo);
			}
		}

		const nodes = response.data?.search?.results.edges.map((edge) => edge.node) || [];
		return {
			fetchResults: processAndValidateNodes(nodes),
			fetchEntitiesRequestId: response.extensions?.gateway?.request_id,
			fetchEntitiesTraceId: response.extensions?.gateway?.trace_id,
			errors: response.errors || [],
		};
	} catch (error: any) {
		// Network failure caught here
		if (networkCallbacks) {
			networkCallbacks.onError(
				error.name || 'NetworkError',
				error.message || 'Failed to fetch search results',
			);
		}
		// Return empty results to allow graceful degradation
		return {
			fetchResults: [],
			errors: [],
			fetchEntitiesRequestId: undefined,
			fetchEntitiesTraceId: undefined,
		};
	}
};

/**
 * The experiment layers are mapped to ensure permitted attributes are sent into search requests.
 */
const mapExperimentLayers = (experimentLayers?: SearchExperimentLayer[]) =>
	experimentLayers?.map(({ name, layerId, shadowId, definitions }) => ({
		name,
		layerId,
		shadowId,
		definitions,
	}));

export const fetchJiraIssuesAndTypeEntities: (
	aggAbsoluteUrl: string | undefined,
	useCaseId: SearchUseCaseId,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	isJiraIssueIndexed: boolean | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	typeFilterEntities: UseTypeEntitiesResult,
	sessionId?: string,
	hasEditOrViewPlanPermission?: boolean,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => Promise<{
	fetchResults: EssentialSearchResult[];
	fetchEntitiesRequestId: (string | null) | undefined;
	fetchEntitiesTraceId: (string | null) | undefined;
	errors: import('@atlassian/search-client').AggError[];
	autocompleteSuggestions: string[] | undefined;
}> = async (
	aggAbsoluteUrl: string | undefined,
	useCaseId: SearchUseCaseId,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	isJiraIssueIndexed: boolean | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	typeFilterEntities: UseTypeEntitiesResult,
	sessionId?: string,
	hasEditOrViewPlanPermission?: boolean,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => {
	const {
		commonFilters,
		confluenceFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		jiraFilters,
		assetsFilters,
	} = selectedFilters || getEmptySelectedFilters();
	const { selectedEntities, entitiesForQuery } = typeFilterEntities;

	const gitHub3PExperiment = serveGitHub3PInJiraQuickFind(thirdPartyConfigs);

	// This fetcher always searches Jira issues, so evaluating the experiment here only fires
	// exposure for Jira-relevant Quick Find searches. When enabled, the SearchDialogQuery emits the
	// lighter value-field Jira issue hydration (see serveQfFastJiraHydrationExperiment).
	const fastJiraHydration = serveQfFastJiraHydrationExperiment();

	const supportedEntities: EntityATI[] = [
		JIRA_ISSUE_ENTITY_ATI,
		JIRA_PROJECT_ENTITY_ATI,
		JIRA_BOARD_ENTITY_ATI,
		JIRA_FILTER_ENTITY_ATI,
		JIRA_DASHBOARD_ENTITY_ATI,
	];
	if (hasEditOrViewPlanPermission) {
		supportedEntities.push(JIRA_PLAN_ENTITY_ATI);
	}

	const entities: EntityATI[] = selectedEntities.length
		? supportedEntities.filter((supportedEntity) => entitiesForQuery.includes(supportedEntity))
		: [JIRA_ISSUE_ENTITY_ATI];

	// GitHub entities are appended directly to the final entities array because they are
	// not part of Jira's type mapping in useTypeEntities, so they would be lost if included
	// in supportedEntities (filtered out on the type-filter path, absent on the default path).
	if (gitHub3PExperiment.serveGitHub3P) {
		entities.push(
			...DEFAULT_CLOUD_GRAPH_DEVINFO_REPOSITORY_ENTITY_ATI,
			...DEFAULT_CLOUD_GRAPH_DEVINFO_PULL_REQUEST_ENTITY_ATI,
		);
	}

	let gitHubThirdPartyFilters: ThirdPartyFilters = gitHub3PExperiment.serveGitHub3P
		? getGitHubThirdPartyFiltersForQuickFind(thirdPartyConfigs)
		: thirdPartyFilters;

	// Scope the document entity to GitHub only via thirdPartyProducts[github].entities.
	// Quick Find code search is legacy-only: in code search M1 mode only Full Page Search
	// serves code, so this path stays off.
	if (gitHub3PExperiment.serveGitHub3P && isLegacyCodeSearchEnabled()) {
		gitHubThirdPartyFilters = addEntityToGitHubProduct(
			gitHubThirdPartyFilters,
			'ati:cloud:graph:document',
		);
	}

	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience,
		tenantId: cloudId,
		isJiraIssueIndexed,
		thirdPartyFilters: gitHubThirdPartyFilters,
	});

	setRequestedBackendExperiments(backendExperiment);

	const { experimentLayers } = backendExperiment || {};

	const variables = {
		experience,
		useCaseId,
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:jira::site/${cloudId}`,
		tenantId: cloudId,
		limit: 12,
		commonFilters,
		confluenceFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters: gitHubThirdPartyFilters,
		entities,
		sort: [],
		searchSessionId,
		sessionId,
		sourceProduct,
		experimentLayers: mapExperimentLayers(experimentLayers),
		includeSocialSignals: false,
		includeJiraIssueType: true,
		// rovo_qf_jira_fast_hydration: emit the lighter value-field Jira issue hydration via
		// @include/@skip in the query (mirrors includeSocialSignals). Evaluated here because this
		// fetcher always searches Jira issues, so exposure only fires for Jira-relevant searches.
		includeFastJiraHydration: fastJiraHydration,
		includeSpaceSubtype: false,
		assetsFilters,
		skipFieldsFor3pInQuickFind: false,
	} satisfies SearchPageQueryVariables;

	const headers = buildMergedHeaders(cloudId, activationId, sourceProduct, productDataRegions);

	networkCallbacks?.onStart();

	try {
		const response = await searchDialogQuery({
			variables,
			aggAbsoluteUrl,
			headers,
		});

		const traceInfo = extractTraceInfoFromResponse(response);

		if (networkCallbacks) {
			const validationResult = validateJiraIssuesResponse(response);
			if (!validationResult.success) {
				networkCallbacks.onError(
					validationResult.error.name,
					validationResult.error.message,
					traceInfo,
				);
			} else {
				networkCallbacks.onSuccess(traceInfo);
			}
		}

		const nodes = response.data?.search?.results.edges.map((edge) => edge.node) || [];
		const autocompleteSuggestions = response.data?.search?.results.autocompleteSuggestions;
		return {
			fetchResults: processAndValidateNodes(nodes),
			fetchEntitiesRequestId: response.extensions?.gateway?.request_id,
			fetchEntitiesTraceId: response.extensions?.gateway?.trace_id,
			errors: response.errors || [],
			autocompleteSuggestions,
		};
	} catch (error: any) {
		// Network failure caught here
		if (networkCallbacks) {
			networkCallbacks.onError(
				error.name || 'NetworkError',
				error.message || 'Failed to fetch search results',
			);
		}
		// Return empty results to allow graceful degradation
		return {
			fetchResults: [],
			errors: [],
			fetchEntitiesRequestId: undefined,
			fetchEntitiesTraceId: undefined,
			autocompleteSuggestions: undefined,
		};
	}
};

export const fetchJiraEntities: (
	aggAbsoluteUrl: string | undefined,
	useCaseId: SearchUseCaseId,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	isJiraIssueIndexed: boolean | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	hasEditOrViewPlanPermission?: boolean,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => Promise<{
	fetchResults: EssentialSearchResult[];
	errors: import('@atlassian/search-client').AggError[];
	autocompleteSuggestions: string[] | undefined;
}> = async (
	aggAbsoluteUrl: string | undefined,
	useCaseId: SearchUseCaseId,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	isJiraIssueIndexed: boolean | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	hasEditOrViewPlanPermission?: boolean,
	networkCallbacks?: NetworkCallbacks,
	activationId?: string,
) => {
	const {
		commonFilters,
		confluenceFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		jiraFilters,
		assetsFilters,
	} = selectedFilters || getEmptySelectedFilters();

	const entities: EntityATI[] = [
		JIRA_BOARD_ENTITY_ATI,
		JIRA_FILTER_ENTITY_ATI,
		JIRA_PROJECT_ENTITY_ATI,
		JIRA_DASHBOARD_ENTITY_ATI,
	];

	if (hasEditOrViewPlanPermission) {
		entities.push(JIRA_PLAN_ENTITY_ATI);
	}
	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience,
		tenantId: cloudId,
		isJiraIssueIndexed,
	});

	setRequestedBackendExperiments(backendExperiment);

	const { experimentLayers } = backendExperiment || {};

	const variables = {
		experience,
		useCaseId,
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:jira::site/${cloudId}`,
		tenantId: cloudId,
		limit: 5,
		commonFilters,
		confluenceFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		assetsFilters,
		entities,
		sort: [],
		searchSessionId,
		sessionId,
		sourceProduct,
		experimentLayers: mapExperimentLayers(experimentLayers),
		includeSocialSignals: false,
		includeJiraBoardContainer: true,
		includeSpaceSubtype: false,
	} satisfies SearchPageQueryVariables;

	const headers = buildMergedHeaders(cloudId, activationId, sourceProduct, productDataRegions);

	networkCallbacks?.onStart();

	try {
		const response = await searchDialogQuery({
			variables,
			aggAbsoluteUrl,
			headers,
		});

		const traceInfo = extractTraceInfoFromResponse(response);

		if (networkCallbacks) {
			const validationResult = validateJiraEntitiesResponse(response);
			if (!validationResult.success) {
				networkCallbacks.onError(
					validationResult.error.name,
					validationResult.error.message,
					traceInfo,
				);
			} else {
				networkCallbacks.onSuccess(traceInfo);
			}
		}

		const nodes = response.data?.search?.results.edges.map((edge) => edge.node) || [];
		const autocompleteSuggestions = response.data?.search?.results.autocompleteSuggestions;
		return {
			fetchResults: processAndValidateNodes(nodes),
			errors: response.errors || [],
			autocompleteSuggestions,
		};
	} catch (error: any) {
		// Network failure caught here
		if (networkCallbacks) {
			networkCallbacks.onError(
				error.name || 'NetworkError',
				error.message || 'Failed to fetch search results',
			);
		}
		// Return empty results to allow graceful degradation
		return {
			fetchResults: [],
			errors: [],
			autocompleteSuggestions: undefined,
		};
	}
};

/**
 * Validates and maps search result nodes to essential data format.
 * Returns null for invalid nodes.
 */
/**
 * Essential data format for search results after validation and mapping.
 */
export type EssentialSearchResult = {
	id: string;
	title: string;
	data: SearchPageNode;
	type: 'search-result';
};

export const mapEssentialData: (result: SearchPageNode) => EssentialSearchResult | null = (
	result: SearchPageNode,
): EssentialSearchResult | null => {
	// Detect and filter out invalid/empty nodes from API
	// Allow empty strings but not null/undefined
	if (!result || !result.id || result.title == null) {
		return null;
	}

	return {
		id: result.id,
		title: result.title,
		data: result,
		type: 'search-result' as const,
	};
};

/**
 * Processes nodes and filters out invalid ones.
 */
const processAndValidateNodes = (nodes: SearchPageNode[]) => {
	const mappedNodes = nodes.map(mapEssentialData);
	const validNodes = mappedNodes.filter((node): node is NonNullable<typeof node> => node !== null);

	return validNodes;
};
